import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Handle, Position } from '@xyflow/react'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import { getVariantStyles, getSizeStyles } from './variants'

const HANDLE_STYLE = { width: 6, height: 6, opacity: 0 }
const VALUE_FONT_SIZES = { lg: '1.75rem', md: '1.375rem', sm: '1.125rem' }

function FlowGraphMetric ({ data, selected }) {
  const {
    nameValue, nameConfig,
    descriptionValue, descriptionConfig,
    variant = 'default',
    size = 'md',
    color
  } = data

  const { formatter } = useFormattingContext()
  const variantStyles = getVariantStyles(variant, color)
  const sizeStyles = getSizeStyles(size)
  const isFilled = variant !== 'default'

  const formattedName = nameConfig?.format
    ? formatter(nameValue, nameConfig.format)
    : nameValue

  const formattedDesc = descriptionConfig?.format && descriptionValue != null
    ? formatter(descriptionValue, descriptionConfig.format)
    : descriptionValue

  const containerStyle = useMemo(() => ({
    width: sizeStyles.width,
    background: isFilled ? variantStyles.background : 'var(--color-surface-050)',
    border: `2px solid ${selected ? variantStyles.selectedBorderColor : variantStyles.borderColor}`,
    borderRadius: 16,
    padding: sizeStyles.padding,
    boxSizing: 'border-box',
    boxShadow: selected ? '0 0 0 3px rgba(0,0,0,0.08)' : '0 2px 4px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    textAlign: 'center',
    overflow: 'hidden'
  }), [sizeStyles, variantStyles, isFilled, selected])

  const valueStyle = {
    fontWeight: 700,
    fontSize: VALUE_FONT_SIZES[size] || VALUE_FONT_SIZES.md,
    lineHeight: 1.2,
    color: isFilled ? variantStyles.color : variantStyles.background,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ...(descriptionConfig?.style || {})
  }

  const labelStyle = {
    fontSize: '0.6875rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: isFilled ? variantStyles.color : 'var(--color-neutral-500)',
    opacity: isFilled ? 0.85 : 1,
    marginBottom: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ...(nameConfig?.style || {})
  }

  const handleColor = isFilled ? variantStyles.borderColor : 'var(--color-neutral-300)'

  return (
    <div style={containerStyle}>
      <Handle type='target' position={Position.Top} style={{ ...HANDLE_STYLE, background: handleColor }} />
      {formattedName != null && (
        <div className={nameConfig?.className || ''} style={labelStyle}>
          {formattedName}
        </div>
      )}
      {formattedDesc != null && (
        <div className={descriptionConfig?.className || ''} style={valueStyle}>
          {formattedDesc}
        </div>
      )}
      <Handle type='source' position={Position.Bottom} style={{ ...HANDLE_STYLE, background: handleColor }} />
    </div>
  )
}

FlowGraphMetric.propTypes = {
  data: PropTypes.shape({
    nameValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nameConfig: PropTypes.object,
    descriptionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    descriptionConfig: PropTypes.object,
    variant: PropTypes.string,
    color: PropTypes.object,
    size: PropTypes.string
  }),
  selected: PropTypes.bool
}

export default memo(FlowGraphMetric)
